import { useEffect, useRef, useState, type CSSProperties } from 'react'

/**
 * A value that is also its own input.
 *
 * The record has no edit mode, so a field cannot announce itself with a box —
 * that would make the whole surface read as a form. It draws as text, shows a
 * faint ground on hover to say it is yours to change, and takes a rim only
 * while it has focus. Writes on blur and on Enter; Escape puts back what was
 * there, which is the only way out of a half-typed change.
 *
 * An input rather than contenteditable: it is a single line of plain text, and
 * contenteditable would accept pasted markup and need talking out of it.
 *
 * `wrap` swaps the input for a textarea that grows down instead of running
 * out of room sideways — see the prop.
 */
export function LiveText({
  value,
  placeholder,
  onCommit,
  className = '',
  style,
  fit = false,
  wrap = false,
  'aria-label': ariaLabel,
}: {
  value: string
  placeholder: string
  onCommit: (value: string) => void
  className?: string
  style?: CSSProperties
  /**
   * Sizes to what is in it rather than to what contains it.
   *
   * An `<input>` is 20 characters wide by default whatever it holds, which is
   * right for a title and wrong for a chip around a project number: "2609-01"
   * sat in a box nearly three times its own width (Alex, 2026-09-11). Opt-in,
   * because every other field here is a line of prose that should fill.
   *
   * Two mechanisms, and both are wanted: `field-sizing: content` in the
   * stylesheet measures the text exactly, and the `size` attribute below is
   * the fallback where that isn't supported yet — accurate to the character in
   * a monospace face, which is what the callers wanting this wear.
   */
  fit?: boolean
  /**
   * Wraps onto as many lines as it needs, rather than truncating.
   *
   * An `<input>` is one line whatever you do to it, so a long value can only
   * ever end in an ellipsis — and on a 320px phone a show called
   * "Llanfairpwllgwyngyll…" was four characters and a full stop in 35px type,
   * which tells you nothing at all (Alex, 2026-09-14). A name is not a single
   * line of data; it is as long as it is.
   *
   * Enter still commits rather than inserting a newline: this wraps for
   * display and is not multi-line input. `LiveArea` in the project record is
   * the one that genuinely takes paragraphs.
   */
  wrap?: boolean
  'aria-label': string
}) {
  const [draft, setDraft] = useState(value)
  const [focused, setFocused] = useState(false)
  const box = useRef<HTMLTextAreaElement>(null)

  // Height follows content. Reset to auto first or it only ever grows — and
  // re-run on width as well, since the same words need more lines when the
  // column narrows.
  useEffect(() => {
    const el = box.current
    if (!el) return

    const fit = () => {
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    }

    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(el)
    return () => observer.disconnect()
  }, [draft, wrap])

  // The saved value wins whenever it changes underneath — a rolled-back write,
  // or a different project opening into the same field.
  useEffect(() => {
    setDraft(value)
  }, [value])

  // Everything but the element itself is the same either way, so the two
  // branches below share this rather than drifting apart.
  const shared = {
    className,
    'aria-label': ariaLabel,
    value: draft,
    placeholder,
    onChange: (event: { target: { value: string } }) => setDraft(event.target.value),
    onFocus: () => setFocused(true),
    onBlur: () => {
      setFocused(false)
      onCommit(draft.trim())
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Enter commits in both, which is why the wrapping one has to say so:
      // a textarea would otherwise put a newline in a title.
      if (event.key === 'Enter') {
        // Committed here rather than by blurring and letting onBlur do it.
        // The input has always leaned on blur, and in a textarea that turned
        // out not to be reliable — the newline is prevented, the blur is
        // called, and the field keeps focus and commits nothing. Saying what
        // is meant is better than arranging for it to happen.
        //
        // A blur still follows, so the field stops looking focused; onBlur
        // will call onCommit a second time with the same value, and saveField
        // ignores a write that changes nothing.
        event.preventDefault()
        onCommit(draft.trim())
        event.currentTarget.blur()
      }
      if (event.key === 'Escape') {
        setDraft(value)
        // Blur would commit; this puts the original back first so it commits
        // nothing, which is what Escape has to mean.
        window.requestAnimationFrame(() => event.currentTarget?.blur())
      }
    },
    onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
      if (!focused) event.currentTarget.style.background = 'var(--w-fill)'
    },
    onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
      if (!focused) event.currentTarget.style.background = 'transparent'
    },
  }

  if (wrap) {
    return (
      <textarea
        {...shared}
        ref={box}
        rows={1}
        style={{
          width: '100%',
          margin: 0,
          padding: '3px 6px',
          marginLeft: -6,
          borderRadius: 7,
          border: 0,
          outline: focused ? '1px solid var(--w-attention-line)' : '1px solid transparent',
          background: focused ? 'var(--w-pane-flat)' : 'transparent',
          transition: 'background .15s ease, outline-color .15s ease',
          // The point of the whole branch: grow downwards, never sideways, and
          // never hand the reader a scrollbar in place of a name.
          resize: 'none',
          overflow: 'hidden',
          ...style,
        }}
      />
    )
  }

  return (
    <input
      {...shared}
      data-fit={fit ? '' : undefined}
      size={fit ? Math.max(1, (draft || placeholder).length) : undefined}
      style={{
        // Left unset when fitting, so the stylesheet's `width: auto` and the
        // `size` above are what decide — an inline 100% would beat both.
        width: fit ? undefined : '100%',
        margin: 0,
        padding: '3px 6px',
        marginLeft: -6,
        borderRadius: 7,
        border: 0,
        outline: focused ? '1px solid var(--w-attention-line)' : '1px solid transparent',
        background: focused ? 'var(--w-pane-flat)' : 'transparent',
        transition: 'background .15s ease, outline-color .15s ease',
        textOverflow: 'ellipsis',
        ...style,
      }}
    />
  )
}
